import { useQuery } from '@apollo/client';
import { useReactTable } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import { CopyPlusIcon } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';

import { TooltipLabel } from '@eluve/blocks';
import {
  Box,
  Button,
  ColDefBuilder,
  DataTableFilterField,
  HStack,
  ServerDataTable,
  TableSearch,
  Tag,
  VStack,
  useDataTableForQuery,
} from '@eluve/components';
import {
  LlmOutputTypesLookup,
  PromptTemplatesBoolExp,
  PromptTemplatesOrderBy,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';

import { TogglePromptTemplateActiveAction } from './TogglePromptTemplateActiveAction';
import {
  allModeArgsQuery,
  searchPromptTemplatesQuery,
} from './prompt.operations';

type PromptTemplateRow = {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  isCurrentDefault: boolean;
  createdAt: string;
  hasDbTemplate: boolean;
  usersWithDefault: number;
  variantCount: number;
  modelArgsFormatted: string[];
};

const columns = new ColDefBuilder<PromptTemplateRow>()
  .linkSortable('name', (row) => `./${row.id}`)
  .defaultSortable('type')
  .defaultBoolean('isCurrentDefault', 'Is Default')
  .defaultBoolean('isActive', 'Active')
  .dateSortable('createdAt', 'Created At')
  .defaultSortable('variantCount', '# Variants')
  .defaultSortable('usersWithDefault', '# User Default')
  .colDef({
    id: 'modelArgsFormatted',
    accessorKey: 'modelArgsFormatted',
    header: 'Model Type',
    cell: ({ row }) => {
      return (
        <HStack wrap gap={1}>
          {row.original.modelArgsFormatted.map((modelArgs, index) => (
            <Tag
              key={index}
              className="font-semibold"
              size="s"
              pill
              color="gray"
              label={modelArgs}
            />
          ))}
        </HStack>
      );
    },
  })
  .colDef({
    header: 'Actions',
    cell: ({ row }) => {
      const showCloneOption =
        row.original.hasDbTemplate && row.original.isActive;

      return (
        <Box hStack gap={1}>
          <TogglePromptTemplateActiveAction
            promptTemplateId={row.original.id}
          />
          {showCloneOption && (
            <TooltipLabel
              label={`Create a new Prompt Template using '${row.original.name}' as a starting point`}
            >
              <Link to={`./clone/${row.original.id}`}>
                <Button size="icon" variant="outline">
                  <CopyPlusIcon />
                </Button>
              </Link>
            </TooltipLabel>
          )}
        </Box>
      );
    },
  })
  .build();

const convertSearchResultsToRows = (
  results: ResultOf<typeof searchPromptTemplatesQuery>,
): PromptTemplateRow[] => {
  return results.promptTemplates.map<PromptTemplateRow>((p) => {
    const currentDefaultVariant = p.prompt_template_variants.find(
      (variant) => variant.isCurrentDefault,
    );

    const allModelArgs = p.prompt_models.map(
      (model) =>
        `${model.model_args.modelType}${model.model_args.name ? ' - ' + model.model_args.name : ''}`,
    );

    return {
      id: p.id,
      name: p.name,
      type: p.outputType,
      createdAt: p.createdAt,
      isActive: p.isActive ?? false,
      isCurrentDefault: p.isCurrentDefault,
      hasDbTemplate: !isNil(currentDefaultVariant?.template),
      usersWithDefault: p.userSettingsAggregate?.aggregate?.count ?? 0,
      variantCount: p.promptTemplateVariantsAggregate?.aggregate?.count ?? 0,
      modelArgsFormatted: allModelArgs,
    };
  });
};

export const PromptTemplatesList: React.FC = () => {
  const { data: modelArgsList } = useQuery(allModeArgsQuery);

  const modelArgFilters =
    modelArgsList?.llmModelArgs.map((arg) => {
      return {
        label: `${arg.modelType}${arg.name ? ' - ' + arg.name : ''}`,
        value: arg.id,
      };
    }) ?? [];

  const filterFields: DataTableFilterField<PromptTemplateRow>[] = [
    {
      id: 'type',
      label: 'Output Type',
      options: Object.keys(LlmOutputTypesLookup)
        .sort()
        .map((type) => ({
          label: type,
          value: type,
        })),
    },
    {
      id: 'modelArgsFormatted',
      label: 'Enabled Model Types',
      options: modelArgFilters.map((arg) => ({
        label: arg.label,
        value: arg.value,
      })),
    },
    {
      id: 'isActive',
      label: 'Active',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  ];

  const { data, rows, setSearch, search, reactTableOptions, isPending } =
    useDataTableForQuery({
      query: searchPromptTemplatesQuery,
      filterFields,
      convertToRows: convertSearchResultsToRows,
      convertSearchParamsToVariables: ({
        offset,
        limit,
        search,
        sorting,
        filters,
      }) => {
        const filterConditions: PromptTemplatesBoolExp[] = [];

        if (filters.type) {
          filterConditions.push({
            outputType: {
              _in: filters.type as (keyof typeof LlmOutputTypesLookup)[],
            },
          });
        }

        if (filters.isActive) {
          filterConditions.push({
            isActive: { _eq: filters.isActive[0] === 'true' },
          });
        }
        if (filters.modelArgsFormatted) {
          filterConditions.push({
            prompt_models: {
              model_args: {
                id: {
                  _in: [...filters.modelArgsFormatted],
                },
              },
            },
          });
        }

        if (search) {
          filterConditions.push({
            name: {
              _ilike: `%${search}%`,
            },
          });
        }

        return {
          offset,
          limit,
          filter: {
            _and: filterConditions,
          },
          orderBy:
            sorting && sorting.length
              ? sorting.map(({ desc, id }) => ({
                  ...match<keyof PromptTemplateRow, PromptTemplatesOrderBy>(id)
                    .with('type', () => ({ outputType: desc ? 'DESC' : 'ASC' }))
                    .with('usersWithDefault', () => ({
                      user_settingsAggregate: {
                        count: desc ? 'DESC' : 'ASC',
                      },
                    }))
                    .with('variantCount', () => ({
                      prompt_template_variantsAggregate: {
                        count: desc ? 'DESC' : 'ASC',
                      },
                    }))
                    .otherwise(() => ({
                      [id]: desc ? 'DESC' : 'ASC',
                    })),
                }))
              : null,
        };
      },
    });

  const rowCount = data?.promptTemplatesAggregate?.aggregate?.count;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    ...reactTableOptions,
  });

  return (
    <VStack gap={2} className="rounded-md border">
      <TableSearch
        value={search ?? ''}
        onChange={setSearch}
        placeholder="Search by name..."
      />
      <ServerDataTable<PromptTemplateRow>
        table={table}
        filterFields={filterFields}
        isPending={isPending}
      />
    </VStack>
  );
};
